import React, { useMemo } from 'react'

import classnames from 'classnames'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Tabs, Tooltip } from 'antd'
import TextRender from '@/components/TextRender'

const TabPane = Tabs.Pane

import getRepetitionRate from '@/utils/diff'
import styles from './LongCreationDemo.module.scss'

export default React.memo(function LongCreationDemo() {

  const tabs = useMemo(() => {
    return [{
      title: '长文示例1',
      summary: '自动驾驶技术近年来取得了广泛的关注和发展，其中场景理解是关键技术之一。利用深度学习进行自动驾驶的场景理解应用研究，可以帮助车辆对周围环境进行高效准确的感知和理解，从而实现更加智能的驾驶控制。',
      heading: [`深度学习在自动驾驶场景理解中的应用研究`, `深度学习在自动驾驶场景理解应用研究中的重要性`],
      outlines: [
        [`引言`, '自动驾驶技术概述', '深度学习技术介绍', '深度学习在自动驾驶场景理解中的应用', '关键技术分析'],
        [`引言`, '自动驾驶技术', '深度学习在自动驾驶中的应用', '深度学习在场景理解中的应用', '深度学习在目标检测中的应用', '结论']
      ],
      sections: [`引言
        随着人工智能技术的发展，自动驾驶逐渐成为了未来交通领域的热点话题。自动驾驶技术的核心是实现车辆对车辆、车辆对路况、车辆对环境等信息的实时感知和理解，这一过程需要依靠先进的图像处理算法和深度学习技术。

        自动驾驶技术的出现，可以有效减少交通事故的发生率，提升交通运输的效率和舒适度。同时，自动驾驶也是智能制造、智慧城市等领域的重要组成部分，因此，在探索自动驾驶技术的过程中，不断优化和完善深度学习算法的应用以及相关技术的完善至关重要。

        本文将介绍深度学习在自动驾驶场景理解中的应用研究，包括深度学习技术的介绍和其在自动驾驶领域中的基本应用。同时，还将分析自动驾驶技术上的关键技术和未来的展望，希望能够为自动驾驶技术的研究和应用提供参考和启示。
      `, `自动驾驶技术概述
      自动驾驶技术是国际智能交通系统研究的重点项目之一，旨在通过应用现代高新技术改变人类交通出行的模式，提高交通出行的安全性、便捷性和绿色性。随着汽车行业向智能化和电动化发展的趋势不断加强，自动驾驶技术逐渐成为汽车行业发展的重要方向。

      自动驾驶技术可以分为五个级别，即从L0到L5。L0表示完全由人类驾驶，L1表示辅助驾驶，L2表示部分自动驾驶，L3表示有条件的自动驾驶，L4表示高度自动化驾驶，L5则表示完全自动驾驶。目前，随着传感技术、计算机技术、通信技术和人工智能技术的不断发展，自动驾驶技术的发展正朝着完全自动驾驶的方向发展。在自动驾驶领域，深度学习技术发挥了重要的作用。

      总的来说，自动驾驶技术的优势在于提高交通出行的安全性、便捷性和绿色性。其中，安全性是自动驾驶技术最重要的考量因素之一。相比于人类驾驶，自动驾驶技术可以更快速、更准确地对周围环境做出反应。此外，自动驾驶技术能够避免人为因素带来的事故风险。使得交通事故发生率将得到极大降低，同时自动驾驶技术还能提高交通的运营效率和节能环保水平，推进智能城市的建设。但是，自动驾驶技术的普及还需要努力克服技术上的难点和安全问题。在深度学习技术的支撑下，自动驾驶技术的应用场景不断扩展和深化。
      `, `深度学习技术介绍

      深度学习作为一种人工智能技术，是近年来在自动驾驶场景中应用广泛的一种指导性方法。深度学习是由神经网络模型组成的一种多层次结构，可以通过对海量数据进行训练来实现对任务的自动推断和学习。深度学习技术最具有优势的一点是，它可以通过端到端学习方法，跳过人为定义的特征提取环节，直接从原始图像数据中提取高层次、抽象的特征信息。

      其中，深度卷积神经网络（DCNN）是深度学习技术中最典型的代表。深度卷积神经网络通过卷积层和池化层的叠加，可以实现对图像的特征提取和表征。另外，递归神经网络（RNN）和长短时记忆网络（LSTM）等模型也可以用于处理序列数据，尤其是对于基于时间序列的自动驾驶场景，LSTM等模型可以很好地解决车辆行驶的轨迹预测问题。

      除了以上模型，深度学习技术还有很多变体和改进模型，如残差网络（ResNet）、注意力机制（Attention Mechanism）等。在深度学习技术的发展势头下，许多自动驾驶企业都开始通过使用深度学习技术实现各种场景下的驾驶决策。

      总而言之，深度学习技术以其稳定的性能和强大的泛化能力，成为了自动驾驶技术中非常有价值的一种方法，其在自动驾驶场景中的应用已经成为了趋势。

      `, `深度学习作为一种新兴的人工智能技术，已经在自动驾驶领域得到广泛应用。在自动驾驶中，深度学习技术可以提高对场景的理解和决策能力，从而实现更加精准和安全的驾驶。下面将介绍深度学习在自动驾驶场景理解中的应用。

      1. 目标检测

      目标检测是自动驾驶中必不可少的技术，它可以帮助车辆识别道路上的交通标志、行人和其他车辆。深度学习技术可以通过卷积神经网络来实现目标检测。例如，YOLO（You Only Look Once）算法可以在一个神经网络中完成两个任务：分类和定位。该算法可以实现实时目标检测，达到了优秀的准确率和速度。

      2. 语义分割

      语义分割是自动驾驶中另一个重要的技术，它可以将图像中的每个像素分配到不同的类别中。这可以让车辆更好地理解和识别道路上的环境。深度学习技术可以通过卷积神经网络实现语义分割。例如，FCN（Fully Convolutional Network）算法可以将图像像素级别的分类处理。该算法在语义分割领域取得了很好的效果。

      3. 路径规划

      路径规划是指车辆根据当前道路上的情况，选择到达目的地的最佳路线。深度学习技术可以通过对历史道路交通拥堵情况的学习，预测未来道路交通情况，从而指导车辆选择更优的路径。例如，百度Apollo开放平台中提供了智能路径规划API，利用深度学习和时间序列预测技术，在保证时间和路径长度下选择最优的路径。

      4. 自主驾驶

      自主驾驶是自动驾驶的最终目标，即让车辆完全由计算机控制。深度学习技术可以通过对车辆周围环境的学习和理解，实现自主驾驶。例如，特斯拉公司的AutoPilot就是利用深度学习技术实现自主驾驶的。通过对车辆周围环境的感知和理解，AutoPilot可以做出更准确和安全的驾驶决策，从而提高了自动驾驶的性能和安全性。

      深度学习技术在自动驾驶中的应用还有很多，包括行为预测、目标跟踪、图像生成等。需要注意的是，深度学习技术并不是解决所有问题的银弹，还需要结合其他技术一起使用，才能实现更加精准和安全的自动驾驶。

      `, `关键技术分析

      1. 数据采集和预处理技术

      深度学习在自动驾驶场景理解中的应用，离不开对大量数据的采集和预处理。而自动驾驶领域的数据主要包括图像识别和路面信息的感知。因此，数据采集需通过多种传感器如激光雷达、摄像头、雷达等设备进行，而预处理则需要通过去除噪声、完善图像质量等手段，保证模型训练的可靠性。

      2. 感知和交互技术

      为了增强自动驾驶车辆的感知能力，目前自动驾驶领域主要采用多模态传感器(包括视觉、雷达、激光雷达、超声波、GPS等)以及传感器融合技术。同时，交互技术也是自动驾驶领域的重要发展方向，通过车辆间的通信和路边设备的交互，提升安全性和智能化程度。

      3. 深度学习模型的选择和训练技术

      在自动驾驶场景中，深度学习模型需要承担大量任务，如实现目标检测、障碍物辨别等功能。因此，如何选择和训练模型是关键技术之一。当前常用的深度学习算法主要有卷积神经网络(CNN)、循环神经网络(RNN)、长短期记忆网络(LSTM)、自编码器(Autoencoder)等。

      4. 数据增强和模型优化技术

      数据增强技术是提高模型精度的重要手段之一。它能通过旋转、缩放、平移等操作增加数据量，并且在不改变原始图像信息的情况下，弥补数据不足带来的影响。此外，针对深度学习模型中的过拟合和欠拟合等问题需要采用相应的优化技术，如正则化、学习率调整、网络剪枝等方法来解决。

      5. 硬件支持技术

      深度学习算法的训练和推理需要巨大的计算资源，因此需要高性能的硬件平台来支撑。当前主流的硬件平台包括GPU(图形处理器)、FPGA(现场可编程门阵列)、TPU(张量处理器)等，它们均具备高速计算和并行处理等优秀特性，可以大大缩短深度学习模型训练和推理的时间。

      综上所述，深度学习在自动驾驶领域中的应用离不开数据采集预处理、感知交互、模型选择训练、数据增强模型优化和硬件支持等关键技术。今后，随着技术的不断创新和深入发展，这些技术也将逐渐完善，为自动驾驶的发展提供更加可靠和智能化的支撑。

      `]
    }, {
      title: '长文示例2',
      summary: '气候变化对全球农业生产的影响，包含目前主要面临的挑战与可能',
      heading: ['气候变化对全球农业的挑战和前景', '气候变化对全球农业生产的挑战及应对方案'],
      outlines: [
        ['引言', '气候变化对全球农业的影响', '全球农业面临的挑战', '全球农业的发展前景', '应对气候变化的措施', '结论'],
        ['引言', '气候变化对全球农业的影响', '面临的挑战', ' 应对策略', '全球农业发展的前景', '结论'],
      ],
      sections: [
        `引言：

随着全球工业和人类活动的不断发展，气候变化已经成为全人类必须面对的严峻问题。气候变化引发了许多自然灾害和人类健康问题，也给全球农业产生了巨大的影响。全球农业作为人类社会的重要基础产业，它所遭受的影响也是前所未有的。本文将就气候变化对全球农业的影响、全球农业面临的挑战以及该行业的发展前景和应对气候变化的措施进行论述，以期更好地探讨气候变化带来的全球问题。

本文将会包括以下内容：

1.1 气候变化对全球农业的影响

1.2 全球农业面临的挑战

1.3 全球农业的发展前景

1.4 应对气候变化的措施`, `目前，气候变化成为全球共同面临的问题之一，其对全球农业的影响日益明显。以下是气候变化对全球农业的影响：

1. 气候变化导致极端天气现象增加

气候变化造成的极端天气现象，如洪涝、干旱、飓风等，对农业生产造成直接的影响。洪涝灾害破坏农作物，干旱导致农作物生长困难，飓风也会导致农业设施损坏。

2. 气候变化导致温度升高

全球气温持续升高，使得一些地区的农业生产逐渐降低。一些农作物需要在一定的温度范围内才能生长，温度过高会导致作物生长不良，甚至死亡。

3. 气候变化导致水资源短缺

水资源是农业生产中至关重要的因素。气候变化导致部分地区的水资源短缺，使得农业生产受到限制。特别是在干旱地区，水资源短缺已经成为制约农业生产的重要因素。

4. 气候变化导致农业生产的变异性

气候变化会导致农业生产的不稳定性增加。气候变化使得农业生产周期发生变化，使得农民很难确定合适的农作物种植时间。这进一步影响了农业生产的效益。

总之，气候变化对全球农业产生了明显的不良影响。农业生产受到的挑战也越来越大，但是未来还是有发展机遇的。下一步我们需要找到应对气候变化的方法，同时采取措施，尽可能减少气候变化带来的不良影响。
`, `
全球农业面临的挑战

气候变化给全球农业带来了很大的挑战。一些气候变化的影响已经显现出来，例如日益频繁的极端气候事件，如干旱、洪水、风暴等，这些事件对于农业生产造成了严重的影响。此外，全球变暖还导致了更长、更热的生长季节和更高的温度，这使得一些农作物的产量和质量下降。由于全球变暖现象，土地生产力的萎缩也会导致农作物产量减少。 根据国际研究，如果地球温度上升1°C，大约有一半的生物多样性会消失，粮食产量会减少10%至15%。

另外，全球变暖也会加剧水资源的短缺，特别是在干旱地区。同时，蒸发作用的增加也会导致土地更加干旱，这样会减少作物的产量和品质。更为严重的是，气候变化会导致大面积的土地退化，这将进一步限制农业的发展。

除了气候相关因素，全球农业还面临一些结构性问题。例如，农业土地的减少和城市化对农业土地的挤压，使得农民的耕作条件更加恶劣。此外，一些较为落后地区的农民面临的经济困难也有可能限制农业的发展。

总的来说，全球农业面临的挑战十分严峻。气候变化和结构性问题都对农业产生了极大的影响。如果要保证全球的粮食安全和人们的生计，那么必须通过采取有效措施来应对这些挑战。
`, `
四、全球农业的发展前景

虽然气候变化对全球农业带来了巨大的挑战，但是也有一些积极的预期。以下是一些可能的发展前景：

1. 科技创新

随着技术的不断进步，越来越多的新技术被应用于农业生产过程中。例如，越来越多的智能农业产品被推出，这些产品使用传感器和数据分析来优化农业生产。而基因编辑技术也可以用于改良农作物或动物品种，使其更适应高温、干旱等恶劣气候条件。

2. 水资源管理的改善

农业生产是世界上最大的用水行业之一，因此水资源管理在农业生产中至关重要。随着技术的发展，越来越多的节水技术被应用于农业生产中。例如，雨水收集系统、滴灌系统和喷灌系统可以减少水的消耗。此外，国际组织也在努力推动水资源的可持续利用。

3. 可持续农业的普及

可持续农业的概念是指在不危害土地、水、气体和野生动物等资源的情况下，生产出高产量的农作物。这种农业模式不仅有利于保护环境还可以提高农民的收入。然而，目前可持续农业的普及率并不高，需要政府和国际组织的支持和投入。

4. 新兴市场的增长

随着全球人口的增长和城市化的加速，新兴市场的需求量也在不断增加。农业是经济发展的基础，因此在新兴市场中，农业的需求也会不断增长。这将促进全球农业的发展，同时也提供了发展的机会。

总之，气候变化对全球农业带来了很大的挑战，但也提供了发展的机会。如果政府和国际组织加大投入力度，积极应对气候变化所带来的挑战，采取科技创新、水资源管理、可持续农业普及和新兴市场发展等措施，全球农业的发展前景将是充满希望的。
`, `
应对气候变化的措施

随着气候变化对全球农业产生越来越大的影响，各国开始制定应对气候变化的措施，以保障农业的可持续发展和粮食安全。下面将分别介绍几项主要的措施。

1. 推广节水农业

水资源是农业发展的关键因素，而气候变化对水资源的影响远比其他因素更为显著。推广节水农业是保障粮食安全的重要举措之一。有效的节水农业技术包括滴灌、喷灌、穴盘灌等方式，以及选择节水型作物、制定科学的灌溉方案。

2. 推广耐旱抗灾作物

气候变化导致的干旱灾害是农业生产的一大挑战，因此推广耐旱抗灾作物是应对气候变化的重要策略。该类作物的种植需要选用具备水分利用率高、耐热耐旱等特性的品种，如玉米、大豆、水稻等。

3. 发展农业碳汇项目

农业碳汇项目是农民通过采取一系列措施，如改善土壤质量、发展低碳农业、种植树木等，将二氧化碳从大气中吸收并尽可能长时间储存的项目。农业碳汇项目可为农民提供经济效益的同时，也具有环境效益。

4. 制定应对气候变化的政策

政府应当采取积极措施，如制定政策、配额、税收、补贴等激励措施，鼓励农民大力开展低碳农业、优化农作物结构、改善土地利用等，促进农业的健康、可持续发展。

5. 加强气候监测和预警

气候监测与预警系统对农业生产的发展和管理至关重要，它能够提供农业生产所需的气象数据和气候预测，帮助农民及时、准确地制定农业生产计划，抵御气候变化的冲击。

`, `
结论：

随着全球气候变化的不断加剧，全球农业面临着前所未有的挑战。农业的可持续发展将受到严重的影响，需要采取有效措施来应对这些挑战。虽然面临很多问题，但是全球农业依然有着广阔的发展前景。以下是本文的结论：

1. 气候变化对全球农业产生了严重的影响，包括物理和生态影响，同时影响了粮食供应、农业产出和其他农业资源等。

2. 全球农业面临的挑战包括：气候不稳定、降雨不足、生物多样性下降、环境污染等问题。这些问题会对全球农业生产、粮食供应、经济发展、人类健康和社会稳定等方面带来不利影响。

3. 为了实现可持续农业发展，应采取有效的措施应对气候变化的挑战。这些措施包括改善农业基础设施、适应气候变化的农业管理、提高农业生产效率、推广农业科技、保护自然环境等。

4. 虽然农业面临很多挑战，但是全球农业发展前景依然广阔。随着全球人口不断增长，对粮食和其他农产品的需求将不断增加。同时，随着农业科技的不断进步，农业生产效率和质量也将得到极大提升。

5. 未来，全球农业应加强区域合作，探索新的合作方式并加强现有的国际农业合作，以应对气候变化。同时，全球农业应注意遵循可持续农业发展规划，确保农业的可持续发展，创造更好的生产条件和更高的生产效率。

`
      ]
    }]
  }, [])

  const items = useMemo(() => {
    return tabs.map((item, index) => {
      return {
        label: item.title,
        key: item.title,
        children: (
          <div className={styles.messages}>
            <div className={classnames(styles.message, styles.user)}>
              {item.summary}
            </div>
            <div className={classnames(styles.message, styles.assistant)}>
              {
                item.heading.map((heading, index) => {
                  return (
                    <div key={index} className={styles.title}>
                      <h3>推荐标题{index + 1}</h3>
                      <p>{ heading }</p>
                    </div>
                  )
                })
              }
            </div>
            <div className={classnames(styles.message, styles.user)}>
              <h3>已选标题</h3>
              {item.heading[0]}
            </div>

            <div className={classnames(styles.message, styles.assistant)}>
              {
                item.outlines.map((outline, index) => {
                  return (
                    <div key={index} className={styles.title}>
                      <h3>推荐大纲{index + 1}</h3>
                      {
                        outline.map((line, lineIndex) => {
                          return (
                            <p key={lineIndex}>{line}</p>
                          )
                        })
                      }
                    </div>
                  )
                })
              }
            </div>

            <div className={classnames(styles.message, styles.user)}>
              {
                <div className={styles.title}>
                  <h3>已选大纲</h3>
                  {
                    item.outlines[0].map((line, lineIndex) => {
                      return (
                        <p key={lineIndex}>{line}</p>
                      )
                    })
                  }
                </div>
              }
            </div>

            <div className={classnames(styles.message, styles.assistant)}>
              <h3>{item.heading[0]}</h3>
              {
                item.outlines[0].map((line, lineIndex) => {
                  return (
                    <div key={lineIndex} className={styles.section}>
                      <h4>{line}</h4>
                      <TextRender text={item.sections[lineIndex]}/>
                    </div>
                  )
                })
              }
            </div>
          </div>
        )
      }
    })
  }, [tabs])

  return (
    <div className={styles.container}>
      <Tabs centered className={styles.tabs} items={items}/>
    </div>
  )
})
